import { useEffect, useState } from 'react'

import { useConnect } from 'redux-bundler-hook'

import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Box, Collapse, Divider, Popover, Tooltip, Typography } from '@mui/material'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { alpha, styled } from '@mui/material/styles'

import { useSmallScreen } from '@common/utils'
import { homeUrls } from '@portal/pages/Home'

const StyledListItem = styled(ListItem)({
  '& .Mui-selected': { backgroundColor: '#4840ba !important' },
  '&:hover': {
    backgroundColor: `${alpha('#4840ba', 0.5)} !important`,
  },
})

const urlMatches = (current, other) => {
  if (current === homeUrls.home || other === homeUrls.home) {
    return current === other
  }
  return current.indexOf(other?.replace(/\?.*$/, '')) === 0
}

const urlIsCurrent = (url, currentURL, childrenURLs) =>
  urlMatches(currentURL, url) ||
  childrenURLs.some((listUrl) => urlMatches(currentURL, listUrl))

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {boolean} props.open
 * @param {Object} props.icon
 * @param {string} props.label
 * @param {string} [props.href]
 * @param {Object[]} [props.subitems]
 * @param {string} [props.subitems[].label]
 * @param {string} [props.subitems[].href]
 * @param {string} props.currentURL
 */
function SidebarMenuItem({
  open,
  icon,
  label,
  href = undefined,
  subitems = [],
  currentURL,
}) {
  const { drawerCollapsed, doCloseDrawer } = useConnect(
    'selectDrawerCollapsed',
    'doCloseDrawer',
  )

  const isSmallScreen = useSmallScreen()

  const [openNestedList, setOpenNestedList] = useState(false)
  const [submenuPopoverAnchor, setSubmenuPopoverAnchor] = useState(false)

  const IconComponent = icon
  const hasChildren = subitems.length

  let ExpandIcon = null
  if (hasChildren) {
    ExpandIcon = openNestedList ? ExpandLess : ExpandMore
  }

  useEffect(() => {
    if (
      open &&
      !openNestedList &&
      urlIsCurrent(
        href,
        currentURL,
        subitems.map((subitem) => subitem.href),
      )
    )
      setOpenNestedList(true)
  }, [currentURL, href, subitems, open])

  const itemOnClick = (event) => {
    if (hasChildren) {
      setOpenNestedList(drawerCollapsed ? true : !openNestedList)
      if (drawerCollapsed) {
        setSubmenuPopoverAnchor(event.currentTarget)
      }
    } else if (isSmallScreen.mobile) {
      doCloseDrawer()
    }
  }

  const subitemOnClick = () => {
    if (isSmallScreen.mobile) {
      doCloseDrawer()
    } else if (submenuPopoverAnchor) {
      setSubmenuPopoverAnchor(null)
    }
  }

  const isActive = currentURL === href

  return (
    <>
      <Popover
        open={!!submenuPopoverAnchor}
        onClose={() => setSubmenuPopoverAnchor(null)}
        anchorEl={submenuPopoverAnchor}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: -10 }}
      >
        <Box sx={{ backgroundColor: 'background.dark', color: 'white' }}>
          <Typography variant="h6" sx={{ px: 2, py: 1, userSelect: 'none' }}>
            {label}
          </Typography>
          <Divider sx={{ mb: 1, backgroundColor: 'grey.500' }} />
          <List component="nav" disablePadding>
            {subitems.map((subitem) => {
              const isSubitemActive = subitem.href === currentURL
              return (
                <StyledListItem key={subitem.label} disablePadding>
                  <ListItemButton
                    key={subitem.label}
                    href={isSubitemActive ? null : subitem.href}
                    disableRipple={isSubitemActive}
                    selected={urlIsCurrent(subitem.href, currentURL, [])}
                    sx={{ cursor: isSubitemActive ? 'default' : 'pointer' }}
                    onClick={subitemOnClick}
                  >
                    <ListItemText disableTypography>
                      <Typography variant="body2">{subitem.label}</Typography>
                    </ListItemText>
                  </ListItemButton>
                </StyledListItem>
              )
            })}
          </List>
        </Box>
      </Popover>
      <StyledListItem disablePadding>
        <ListItemButton
          href={isActive ? null : href}
          selected={urlIsCurrent(
            href,
            currentURL,
            subitems.map((subitem) => subitem.href),
          )}
          onClick={itemOnClick}
          disableRipple={isActive}
          sx={{ cursor: isActive ? 'default' : 'pointer' }}
        >
          {open ? (
            <ListItemIcon>
              <IconComponent sx={{ color: 'white' }} />
            </ListItemIcon>
          ) : (
            <Tooltip placement="right" title={label}>
              <ListItemIcon>
                <IconComponent sx={{ color: 'white' }} />
              </ListItemIcon>
            </Tooltip>
          )}
          {open && (
            <ListItemText disableTypography>
              <Typography variant="body2">{label}</Typography>
            </ListItemText>
          )}
          {open && ExpandIcon && (
            <ExpandIcon sx={{ color: 'grey.400', fontSize: 18 }} />
          )}
        </ListItemButton>
      </StyledListItem>
      {open && hasChildren ? (
        <Collapse in={openNestedList} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subitems.map((subitem) => {
              const isSubitemActive = subitem.href === currentURL
              return (
                <StyledListItem key={subitem.label} disablePadding>
                  <ListItemButton
                    key={subitem.label}
                    href={isSubitemActive ? null : subitem.href}
                    disableRipple={isSubitemActive}
                    selected={urlIsCurrent(subitem.href, currentURL, [])}
                    sx={{
                      ml: 4,
                      borderLeft: '2px solid',
                      borderLeftColor: 'grey.300',
                      cursor: isSubitemActive ? 'default' : 'pointer',
                    }}
                    onClick={subitemOnClick}
                  >
                    <ListItemText disableTypography>
                      <Typography variant="body2">{subitem.label}</Typography>
                    </ListItemText>
                  </ListItemButton>
                </StyledListItem>
              )
            })}
          </List>
        </Collapse>
      ) : null}
    </>
  )
}

export default SidebarMenuItem
